import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { Gate } from "./features/auth/Gate";
import { createApiClient, retryTransient, TRPCProvider } from "./lib/api";
import { firebaseTokenSource } from "./lib/auth-token";
import { ClientsProvider } from "./lib/clients";
import { initFirebase } from "./lib/firebase";
import { presenceStore } from "./lib/presence";
import { loadRuntimeConfig } from "./lib/runtime-config";
import { createAppRouter } from "./router";
import "./styles.css";

const container = document.getElementById("root");
if (container === null) throw new Error("#root is missing from index.html");
const root = createRoot(container);

// A tab left open across a deploy asks for a code chunk the new build no longer has: reload once
// to pick up the new build instead of breaking the feature that needed it.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  window.location.reload();
});

async function start() {
  const config = await loadRuntimeConfig(window.fetch.bind(window));
  const firebase = initFirebase(config);
  const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, retry: retryTransient } } });
  const api = createApiClient(config.apiUrl, firebaseTokenSource(firebase.auth), presenceStore);
  const router = createAppRouter();

  root.render(
    <StrictMode>
      <ClientsProvider value={{ config, firebase }}>
        <QueryClientProvider client={queryClient}>
          <TRPCProvider trpcClient={api} queryClient={queryClient}>
            <Gate>
              <RouterProvider router={router} />
            </Gate>
          </TRPCProvider>
        </QueryClientProvider>
      </ClientsProvider>
    </StrictMode>,
  );
}

start().catch((error: unknown) => {
  const message = error instanceof Error ? error.message : String(error);
  root.render(
    <main className="grid min-h-dvh place-items-center bg-bg px-4">
      <div role="alert" className="max-w-md text-sm text-danger">
        <h1 className="mb-2 text-lg font-semibold text-text">KitWork+ could not start</h1>
        <p>{message}</p>
      </div>
    </main>,
  );
});
